import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Timeline } from "@/components/Timeline";
import { TurfProjects } from "@/components/TurfProjects";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Droplets, TreePine, Mail, ChevronRight, Archive } from "lucide-react";
import { tabClass } from "@/lib/utils";

const FILTER_TABS = [
  { key: "all", label: "All Crops" },
  { key: "trees", label: "Fruit Trees" },
  { key: "vines", label: "Vines & Berries" },
  { key: "annuals", label: "Annuals & Vegetables" },
];
const CATEGORY_GROUPS = {
  all: () => true,
  trees: (c) => ["tree", "perennial"].includes(c),
  vines: (c) => ["vine", "berry"].includes(c),
  annuals: (c) => c === "annual",
};

export default function ConsumerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trees, setTrees] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [yields, setYields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/trees"), api.get("/notifications"), api.get("/yield-history")])
      .then(([t, n, y]) => { setTrees(t.data); setNotifs(n.data); setYields(y.data); })
      .finally(() => setLoading(false));
    api.get("/branding").then((r) => setBrand(r.data.white_label ? r.data : null)).catch(() => {});
  }, []);

  const active = trees[0];
  const filteredTrees = trees.filter((t) => CATEGORY_GROUPS[filter](t.category));

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {brand && (
          <div data-testid="managed-by-banner" className="mb-6 flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 shadow-[0_8px_32px_rgba(0,0,0,0.04)]"
               style={{ borderLeft: `4px solid ${brand.accent_color}` }}>
            {brand.logo
              ? <img src={brand.logo} alt={brand.brand_name} className="h-8 max-w-[140px] object-contain" />
              : <span className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white" style={{ backgroundColor: brand.accent_color }}>{brand.brand_name.charAt(0)}</span>}
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400">Your grove is managed by</p>
              <p className="text-sm font-semibold" style={{ color: brand.accent_color }} data-testid="managed-by-name">{brand.brand_name}</p>
            </div>
          </div>
        )}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-emerald-700">Grower dashboard</span>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-emerald-950">Your grove, {user.name.split(" ")[0]}</h1>
          </div>
          <Button data-testid="new-tree-btn" onClick={() => navigate("/trees/new")} className="rounded-full h-12 px-6 bg-emerald-900 hover:bg-emerald-800 text-white">
            <Plus className="w-4 h-4 mr-1" /> New plant / crop profile
          </Button>
        </div>

        <TurfProjects />

        {loading ? (
          <p className="mt-10 text-stone-400 font-serif text-2xl">Loading grove…</p>
        ) : trees.length === 0 ? (
          <div data-testid="empty-state" className="mt-10 rounded-3xl border border-dashed border-stone-300 bg-white p-16 text-center">
            <TreePine className="w-10 h-10 text-emerald-700 mx-auto" />
            <h3 className="mt-4 font-serif text-2xl text-emerald-950">No plants yet</h3>
            <p className="mt-2 text-stone-500">Create your first plant / crop profile to generate a 2-year care calendar.</p>
            <Button data-testid="empty-new-tree" onClick={() => navigate("/trees/new")} className="mt-6 rounded-full bg-emerald-900 text-white hover:bg-emerald-800">Create plant / crop profile</Button>
          </div>
        ) : (
          <>
            {/* HERO timeline */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-3xl border border-black/5 bg-white p-6 lg:p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-400">Active calendar</p>
                  <h2 className="font-serif text-2xl text-emerald-950">{active.name} · <span className="text-stone-500">{active.tree_type}</span></h2>
                </div>
                <Badge className="rounded-full bg-emerald-100 text-emerald-800 border-emerald-200">{active.progress}% complete</Badge>
              </div>
              <Timeline milestones={active.milestones} onSelect={() => navigate(`/trees/${active.id}`)} />
            </motion.div>

            <div className="mt-8 grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                {/* Category filter */}
                <div data-testid="crop-filter" className="mb-5">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-stone-400 mb-2">Filter by Crop Type</p>
                  <div className="flex flex-wrap gap-2">
                    {FILTER_TABS.map((f) => {
                      const count = trees.filter((t) => CATEGORY_GROUPS[f.key](t.category)).length;
                      return (
                        <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)} className={tabClass(filter === f.key)}>
                          {f.label} <span className={`ml-1 text-xs ${filter === f.key ? "text-emerald-200" : "text-emerald-500"}`}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                {filteredTrees.length === 0 && (
                  <p data-testid="filter-empty" className="text-sm text-stone-400 sm:col-span-2 rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center">No plants in this category yet.</p>
                )}
                {filteredTrees.map((t) => (
                  <button key={t.id} data-testid={`tree-card-${t.id}`} onClick={() => navigate(`/trees/${t.id}`)}
                    className="text-left rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:border-emerald-300 transition-colors">
                    <div className="flex items-center justify-between">
                      <TreePine className="w-6 h-6 text-emerald-700" />
                      <div className="flex items-center gap-2">
                        {t.category && <span className="text-[10px] uppercase tracking-[0.15em] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">{t.category}</span>}
                        <ChevronRight className="w-4 h-4 text-stone-300" />
                      </div>
                    </div>
                    <h3 className="mt-3 font-serif text-2xl text-emerald-950">{t.name}</h3>
                    <p className="text-sm text-stone-500">{t.tree_type} · {t.soil_type} · {t.stature}</p>
                    {t.active_milestone && (
                      <div className="mt-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                        <Droplets className="w-4 h-4" /> {t.active_milestone.title}
                      </div>
                    )}
                    <div className="mt-4 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                      <div className="h-full bg-emerald-600" style={{ width: `${t.progress}%` }} />
                    </div>
                  </button>
                ))}
                </div>
              </div>

              <div className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                <h3 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><Mail className="w-5 h-5 text-emerald-700" /> Reminders sent</h3>
                <p className="text-xs text-stone-400 mt-1">Automated split-emails from the dispatch engine</p>
                <div className="mt-4 space-y-3 max-h-[420px] overflow-auto">
                  {notifs.length === 0 && <p className="text-sm text-stone-400">No reminders dispatched yet.</p>}
                  {notifs.map((n) => (
                    <div key={n.id} data-testid={`notif-${n.id}`} className="rounded-xl border border-stone-100 bg-stone-50/60 p-3">
                      <p className="text-sm font-semibold text-emerald-950">{n.subject}</p>
                      <p className="text-xs text-stone-500 mt-1 line-clamp-2">{n.body}</p>
                      <p className="font-mono-ag text-[10px] text-stone-400 mt-1">{new Date(n.sent_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {yields.length > 0 && (
              <div data-testid="yield-history" className="mt-8">
                <h2 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><Archive className="w-5 h-5 text-emerald-700" /> Yield History</h2>
                <p className="text-xs text-stone-400 mt-1">Archived harvests from succession-planted beds</p>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {yields.map((y) => (
                    <div key={y.id} data-testid={`yield-${y.id}`} className="rounded-2xl border border-black/5 bg-white p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-[0.15em] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{y.plant_type}</span>
                        <span className="font-mono-ag text-[10px] text-stone-400">{new Date(y.harvested_at).toLocaleDateString()}</span>
                      </div>
                      <h3 className="mt-2 font-serif text-xl text-emerald-950">{y.crop_name}</h3>
                      <p className="text-sm text-stone-500">{y.tree_type} · {y.total_days_active} days active</p>
                      {y.yield_notes && <p className="mt-2 text-sm text-stone-600 italic">“{y.yield_notes}”</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
